import { useState, useEffect, useRef } from "react";
import { api, apiErr, API } from "@/lib/api";
import { useFetch, Field } from "@/lib/ui";
import { useLang } from "@/context/LangContext";
import { Empty, PageHeader } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { filterGrades } from "@/lib/grades";
import { Plus, Trash2, BookOpen, ShoppingBag } from "lucide-react";

function GradeCard({ fee }) {
  const { L } = useLang();
  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid={`fees-view-${fee.grade_level}`}>
      <p className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><BookOpen className="h-5 w-5 text-primary" />{fee.grade_level}</p>
      {fee.courses.length === 0 ? <p className="text-sm text-muted-foreground">{L("No courses listed.", "Aucun cours listé.")}</p> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-1">{L("Course", "Cours")}</th><th className="py-1 text-right">{L("Price", "Prix")}</th></tr></thead>
          <tbody>
            {fee.courses.map((c, i) => (<tr key={i} className="border-b border-border/50"><td className="py-1.5">{c.name}</td><td className="py-1.5 text-right">${Number(c.price || 0).toFixed(2)}</td></tr>))}
          </tbody>
        </table>
      )}
      <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
        <span className="font-heading font-bold">{L("Total payment due", "Total à payer")}</span>
        <span className="font-heading font-extrabold text-2xl text-emerald-600" data-testid={`fees-total-${fee.grade_level}`}>${Number(fee.total || 0).toFixed(2)}</span>
      </div>
      <p className="text-xs text-emerald-700 mt-1 font-medium" data-testid={`fees-nb-${fee.grade_level}`}>
        {L("N.B. Payable within 5 days of receiving the list (or before).", "N.B. À payer dans les 5 jours suivant la réception de la liste (ou avant).")}
      </p>
      {fee.materials.length > 0 && (
        <div className="mt-4 bg-secondary/40 rounded-lg p-3" data-testid={`fees-materials-${fee.grade_level}`}>
          <p className="font-medium flex items-center gap-2 mb-1"><ShoppingBag className="h-4 w-4" />{L("Other materials to purchase outside school", "Autres matériels à acheter hors de l'école")}</p>
          <p className="text-xs text-muted-foreground mb-2">{L("(Not part of the course payment)", "(Ne fait pas partie du paiement des cours)")}</p>
          <ul className="list-disc pl-5 text-sm space-y-0.5">{fee.materials.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

export default function CourseFees({ manage = false }) {
  const { L } = useLang();
  const { data: fees, loading, reload } = useFetch(() => api.get("/course-fees"));
  const { data: school } = useFetch(() => api.get("/settings"));
  const [grade, setGrade] = useState("");
  const [gq, setGq] = useState("");
  const gradeMatches = filterGrades(gq);
  const [courses, setCourses] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const firstRun = useRef(true);
  const loadedRef = useRef("");
  const norm = (cs, ms) => JSON.stringify({ c: (cs || []).filter((c) => (c.name || "").trim()).map((c) => ({ name: c.name.trim(), price: Number(c.price) || 0 })), m: (ms || []).filter((x) => (x || "").trim()).map((x) => x.trim()) });
  const [autoSaved, setAutoSaved] = useState(false);

  useEffect(() => {
    setConfirmDel(false);
    if (!grade) { setCourses([]); setMaterials([]); loadedRef.current = norm([], []); return; }
    const f = (fees || []).find((x) => x.grade_level === grade);
    setCourses(f ? f.courses.map((c) => ({ ...c })) : []);
    setMaterials(f ? [...f.materials] : []);
    loadedRef.current = norm(f ? f.courses : [], f ? f.materials : []);
  }, [grade, fees]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = courses.reduce((s, c) => s + (Number(c.price) || 0), 0);
  const save = async (silent = false) => {
    if (!grade) { if (!silent) toast.error(L("Select a grade", "Choisir un niveau")); return; }
    const cleanCourses = courses.filter((c) => (c.name || "").trim()).map((c) => ({ name: c.name.trim(), price: Number(c.price) || 0 }));
    const cleanMaterials = materials.filter((m) => (m || "").trim());
    if (cleanCourses.length === 0 && cleanMaterials.length === 0) {
      if (!silent) toast.error(L("Cannot save an empty list. Please fill in the fields or cancel.", "Impossible d'enregistrer une liste vide. Veuillez remplir les champs ou annuler."));
      return;
    }
    setSaving(true);
    try {
      await api.post("/course-fees", { grade_level: grade, courses: cleanCourses, materials: cleanMaterials });
      setAutoSaved(true);
      if (!silent) { toast.success(L("Saved", "Enregistré")); reload(); }
    } catch (e) { if (!silent) toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async () => {
    try {
      await api.del(`/course-fees/${encodeURIComponent(grade)}`);
      toast.success(L("Class list deleted", "Liste de la classe supprimée"));
      setConfirmDel(false); setGrade(""); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    if (!grade) return;
    if (norm(courses, materials) === loadedRef.current) return;
    setAutoSaved(false);
    const t = setTimeout(() => { save(true); }, 900);
    return () => clearTimeout(t);
  }, [grade, courses, materials]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;

  if (!manage) {
    return (
      <div className="animate-fade-up">
        <PageHeader title={L("Course Fees / Book List", "Frais de cours / Liste de livres")}
          description={L("Course materials and payment due for your child's grade.", "Supports de cours et montant à payer pour le niveau de votre enfant.")}
          action={<Button variant="outline" className="print:hidden" onClick={() => window.print()} data-testid="fees-print-btn">{L("Print / PDF", "Imprimer / PDF")}</Button>} />
        <div className="hidden print:block mb-6 text-center" data-testid="fees-print-header">
          {school?.logo_path && <img src={`${API}/settings/logo`} alt="" className="h-16 mx-auto mb-2 object-contain" />}
          <p className="font-heading font-bold text-xl">{school?.school_name || "ICDH"}</p>
          {school?.address && <p className="text-xs text-muted-foreground">{school.address}</p>}
          <p className="text-sm font-medium mt-1">{L("Course Fees / Book List", "Frais de cours / Liste de livres")}</p>
        </div>
        {(fees || []).length === 0 ? <Empty title={L("No list published yet", "Aucune liste publiée")} /> : (
          <div className="space-y-4">{fees.map((f) => <GradeCard key={f.grade_level} fee={f} />)}</div>
        )}
      </div>
    );
  }

  return (
    <div className="animate-fade-up">
      <PageHeader title={L("Course Fees / Book List", "Frais de cours / Liste de livres")}
        description={L("Set the priced course list and the free 'buy-outside' materials list per grade.", "Définissez la liste des cours avec prix et la liste des matériels à acheter à l'extérieur, par niveau.")} />
      <div className="max-w-xs mb-5">
        <Field label={L("Grade", "Niveau")}>
          <Input value={gq} onChange={(e) => setGq(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && gradeMatches.length >= 1) { setGrade(gradeMatches[0]); setGq(""); } }}
            placeholder={L("Search grade (e.g. 7ème AF)", "Rechercher un niveau (ex. 7ème AF)")}
            className="mb-2" data-testid="fees-grade-search" />
          <Select value={grade} onValueChange={(v) => { setGrade(v); setGq(""); }}>
            <SelectTrigger data-testid="fees-grade-select"><SelectValue placeholder={L("Select grade", "Choisir le niveau")} /></SelectTrigger>
            <SelectContent>
              {gradeMatches.length === 0
                ? <div className="px-2 py-1.5 text-sm text-muted-foreground" data-testid="fees-grade-nomatch">{L("No matching grade", "Aucun niveau correspondant")}</div>
                : gradeMatches.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
          {gq && gradeMatches.length >= 1 && grade !== gradeMatches[0] && (
            <button type="button" onClick={() => { setGrade(gradeMatches[0]); setGq(""); }}
              className="mt-1.5 text-xs text-primary font-medium hover:underline" data-testid="fees-grade-quickpick">
              {L("Use", "Utiliser")}: {gradeMatches[0]} ↵
            </button>
          )}
        </Field>
      </div>

      {grade && (
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-lg p-5">
            <p className="font-heading font-bold mb-3">{L("Courses (with price)", "Cours (avec prix)")}</p>
            <div className="space-y-2">
              {courses.map((c, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`fees-course-row-${i}`}>
                  <Input className="flex-1" placeholder={L("Course name", "Nom du cours")} value={c.name} onChange={(e) => setCourses(courses.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} data-testid={`fees-course-name-${i}`} />
                  <Input type="number" className="w-32" placeholder="0.00" value={c.price} onChange={(e) => setCourses(courses.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} data-testid={`fees-course-price-${i}`} />
                  <Button size="icon" variant="ghost" onClick={() => setCourses(courses.filter((_, j) => j !== i))} data-testid={`fees-course-del-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setCourses([...courses, { name: "", price: "" }])} data-testid="fees-add-course"><Plus className="h-4 w-4 mr-1" />{L("Add course", "Ajouter un cours")}</Button>
            <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
              <span className="font-heading font-bold">{L("Total payment due", "Total à payer")}</span>
              <span className="font-heading font-extrabold text-2xl text-emerald-600" data-testid="fees-total-preview">${total.toFixed(2)}</span>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-5">
            <p className="font-heading font-bold mb-1">{L("Other materials (buy outside, no price)", "Autres matériels (achat extérieur, sans prix)")}</p>
            <p className="text-xs text-muted-foreground mb-3">{L("Books, notebooks, pens, etc. — not part of the course payment.", "Livres, cahiers, stylos, etc. — ne fait pas partie du paiement des cours.")}</p>
            <div className="space-y-2">
              {materials.map((m, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`fees-material-row-${i}`}>
                  <Input className="flex-1" placeholder={L("Item (e.g. Notebook)", "Article (ex. Cahier)")} value={m} onChange={(e) => setMaterials(materials.map((x, j) => j === i ? e.target.value : x))} data-testid={`fees-material-name-${i}`} />
                  <Button size="icon" variant="ghost" onClick={() => setMaterials(materials.filter((_, j) => j !== i))} data-testid={`fees-material-del-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setMaterials([...materials, ""])} data-testid="fees-add-material"><Plus className="h-4 w-4 mr-1" />{L("Add material", "Ajouter un matériel")}</Button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {autoSaved && <span className="text-xs text-emerald-600" data-testid="fees-autosaved">{L("Auto-saved ✓", "Enregistré auto. ✓")}</span>}
            <Button onClick={() => save(false)} disabled={saving} data-testid="fees-save-btn">{saving ? L("Saving…", "Enregistrement…") : L("Save list", "Enregistrer la liste")}</Button>
            {!confirmDel ? (
              <Button variant="outline" className="text-destructive border-destructive/40 hover:bg-destructive/10 ml-auto" onClick={() => setConfirmDel(true)} data-testid="fees-delete-btn"><Trash2 className="h-4 w-4 mr-1" />{L("Delete this class", "Supprimer cette classe")}</Button>
            ) : (
              <div className="ml-auto flex flex-wrap items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2" data-testid="fees-delete-confirm-bar">
                <span className="text-sm text-destructive font-medium">{L("Delete all fees for this class?", "Supprimer tous les frais de cette classe ?")}</span>
                <Button size="sm" variant="destructive" onClick={del} data-testid="fees-delete-confirm-btn">{L("Confirm deletion", "Confirmer la suppression")}</Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDel(false)} data-testid="fees-delete-cancel-btn">{L("Cancel", "Annuler")}</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
